import {
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Container,
  Row,
  Col,
  Table,
  Badge,
  Button,
  Tooltip,
} from "reactstrap";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CookieTool } from "utils/CookieTool";
import { toast } from "react-toastify";
import moment from "moment";

import {
  CheckCircle,
  Close,
  Flag,
  RemoveCircle,
  AddBox,
  WatchLater,
  Check,
  IndeterminateCheckBox,
} from "@mui/icons-material";
import { Accordion, AccordionDetails, Divider } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import qualityApi from "service/qualityApi";

import ModalAttendanceAnalysisPrompt from "components/Modal/ModalAttendanceAnalysisPrompt";
import ModalAttendanceAnalysisResponse from "components/Modal/ModalAttendanceAnalysisResponse";
import ModalPromptScoreDetails from "components/Modal/ModalPromptScoreDetails";
import ModalBlockRulesDetails from "components/Modal/ModalBlockRulesDetails";
import ModalCustomAttendanceAnalysis from "components/Modal/ModalCustomAttendanceAnalysis";
import ModalUserAttendanceFeedback from "components/Modal/ModalUserAttendanceFeedback";
import ModalAttendanceReview from "components/Modal/ModalAttendanceReview";
import ModalContestationForm from "components/Modal/ModalContestationForm";
import ModalContestationStatus from "components/Modal/ModalContestationStatus";
import ModalPromptRulesDetails from "components/Modal/ModalPromptRulesDetails";
import ModalLibraryMatchingTerms from "components/Modal/ModalLibraryMatchingTerms";
import ModalLibraryMatchDetails from "components/Modal/ModalLibraryMatchDetails";
import ConsultantFeedbackStatus from "enum/consultant-feedback-status";
import CopyableText from "components/CopyableText";
import AudioPlayer from "components/AudioPlayer";
import Loading from "components/Loading";

import { AccordionSummary } from "assets/css/custom-components";
import { tableHeaderStyle } from "assets/css/custom-components";
import { tableHeaderStyleCenter } from "assets/css/custom-components";
import { tableContentStyle } from "assets/css/custom-components";
import { styledTableScroll } from "assets/css/custom-components";

import { DateTool } from "utils/DateTool";

const getFeedbackStatusLabel = (status) => {
  switch (status) {
    case ConsultantFeedbackStatus.NOTAPPLIED:
      return "Não aplicado";
    case ConsultantFeedbackStatus.PENDING:
      return "Pendente";
    case ConsultantFeedbackStatus.APPROVED:
      return "Aprovado";
    case ConsultantFeedbackStatus.REJECTED:
      return "Rejeitado";
    default:
      return "Não aplicado";
  }
};

const libraryMatchType = {
  EXACT_MATCH: "Exatidão",
  SIMILARITY: "Similaridade",
  LOCATION: "Localização",
};

const libraryMatchSpeaker = {
  0: "Cliente",
  1: "Consultor",
};

const HOLYDAYS = "01-01,04-21,05-01,06-12,09-07,10-12,11-02,11-15,11-20,12-25";

function countHolydaysBetween(start, end) {
  const holydaysArr = HOLYDAYS.split(",");
  let count = 0;
  let current = moment(start).clone();
  const endDate = moment(end);
  while (current.isSameOrBefore(endDate, "day")) {
    if (holydaysArr.includes(current.format("MM-DD"))) {
      count++;
    }
    current.add(1, "day");
  }
  return count;
}

const getBlockScoreRules = (
  scoreBlockResults,
  promptId,
  evaluableEventId = null,
  libraryId = null,
) => {
  if (!scoreBlockResults || scoreBlockResults.length === 0) {
    return null;
  }

  const blockRules = [];

  scoreBlockResults.forEach((block) => {
    // Verifica se é a estrutura antiga (blockConfig)
    if (block.blockConfig && Array.isArray(block.blockConfig)) {
      const matchingConfig = block.blockConfig.find((config) => {
        if (promptId && config.promptId === promptId) {
          return true;
        }
        if (evaluableEventId && config.evaluableEventId === evaluableEventId) {
          return true;
        }
        if (libraryId && config.libraryId === libraryId) {
          return true;
        }
        return false;
      });
      if (matchingConfig && matchingConfig.config) {
        const filteredBlock = {
          ...block,
          blockConfig: block.blockConfig,
        };
        blockRules.push(filteredBlock);
      }
    } else if (
      evaluableEventId &&
      block.evaluableEventsConfig &&
      Array.isArray(block.evaluableEventsConfig)
    ) {
      const matchingEventConfig = block.evaluableEventsConfig.find(
        (config) => config.evaluableEventId === evaluableEventId,
      );

      if (matchingEventConfig && matchingEventConfig.config) {
        const convertedBlockConfig = {
          evaluableEventId: evaluableEventId,
          config: matchingEventConfig.config,
        };

        const filteredBlock = {
          ...block,
          blockConfig: block.evaluableEventsConfig,
        };
        blockRules.push(filteredBlock);
      }
    } else if (
      promptId &&
      block.promptsConfig &&
      Array.isArray(block.promptsConfig)
    ) {
      const matchingPromptConfig = block.promptsConfig.find(
        (config) => config.promptId === promptId,
      );

      if (matchingPromptConfig && matchingPromptConfig.config) {
        const convertedBlockConfig = {
          promptId: promptId,
          config: matchingPromptConfig.config,
        };

        const filteredBlock = {
          ...block,
          blockConfig: [convertedBlockConfig],
        };
        blockRules.push(filteredBlock);
      }
    } else if (
      libraryId &&
      block.librariesConfig &&
      Array.isArray(block.librariesConfig)
    ) {
      const matchingLibraryConfig = block.librariesConfig.find(
        (config) => config.libraryId === libraryId,
      );

      if (matchingLibraryConfig && matchingLibraryConfig.config) {
        const convertedBlockConfig = {
          libraryId: libraryId,
          config: matchingLibraryConfig.config,
        };

        const filteredBlock = {
          ...block,
          blockConfig: [convertedBlockConfig],
        };
        blockRules.push(filteredBlock);
      }
    }
  });

  return blockRules.length > 0 ? blockRules : null;
};

const QualityAttendance = () => {
  const { id: attendanceId } = useParams();

  const [loading, setLoading] = useState(false);

  const [attendance, setAttendance] = useState({});
  const [attendanceName, setAttendanceName] = useState("");
  const [messages, setMessages] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [segmentID, setSegmentID] = useState();
  const [evaluatedHumanPrompt, setEvaluatedHumanPrompt] = useState([]);
  const [evaluableEvent, setEvaluableEvent] = useState([]);
  const [libraryMatches, setLibraryMatches] = useState([]);

  const [updateAttendance, setUpdateAttendance] = useState(false);
  const [orderPromptsBy, setOrderPromptsBy] = useState([]);
  const [orderEvaluableEventsBy, setOrderEvaluableEventsBy] = useState([]);
  const [hasFeedback, setHasFeedback] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [hasReview, setHasReview] = useState(false);
  const [evaluableEventPriority, setEvaluableEventPriority] = useState([]);
  const [contestation, setContestation] = useState([]);
  const [scoreRule, setScoreRule] = useState([]);
  const [slaConfig, setSlaConfig] = useState(null);
  const [deadlineContestation, setDeadlineContestation] = useState(null);
  const [deadlineEvaluableEvent, setDeadlineEvaluableEvent] = useState(null);
  const [scoreBlockRules, setScoreBlockRules] = useState([]);

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const toggleTooltip = () => setTooltipOpen(!tooltipOpen);

  const permissions = CookieTool.getLarge("permissions");
  const questionRegex = /<pergunta>(.*?)<pergunta>/;

  useEffect(() => {
    if (slaConfig && attendance?.createdAt) {
      let contestationDeadline = DateTool.addDays(
        attendance.createdAt,
        slaConfig.timeToContestation.maxDays,
        slaConfig.timeToContestation.businessDays,
      );
      if (slaConfig.timeToContestation.businessDays) {
        const holydaysCount = countHolydaysBetween(
          attendance.createdAt,
          contestationDeadline,
        );
        if (holydaysCount > 0) {
          contestationDeadline = moment(contestationDeadline).add(
            holydaysCount,
            "days",
          );
        }
      }
      setDeadlineContestation(contestationDeadline);

      let evaluableEventDeadline = DateTool.addDays(
        attendance.createdAt,
        slaConfig.timeToEvaluableEvent.maxDays,
        slaConfig.timeToEvaluableEvent.businessDays,
      );
      if (slaConfig.timeToEvaluableEvent.businessDays) {
        const holydaysCount = countHolydaysBetween(
          attendance.createdAt,
          evaluableEventDeadline,
        );
        if (holydaysCount > 0) {
          evaluableEventDeadline = moment(evaluableEventDeadline).add(
            holydaysCount,
            "days",
          );
        }
      }
      setDeadlineEvaluableEvent(evaluableEventDeadline);
    }
  }, [slaConfig, attendance]);

  useEffect(() => {
    if (
      attendanceId &&
      permissions.includes("create:quality:user_attendance_feedback")
    ) {
      qualityApi
        .get(`/user-attendance-feedback/${attendanceId}`)
        .then((response) => response.data)
        .then((response) => {
          if (response.length === 0) {
            setHasFeedback(false);
            return;
          }

          const mostRecentFeedback = response[0];
          setFeedback(mostRecentFeedback);
          if (
            mostRecentFeedback?.status == "PENDING" ||
            mostRecentFeedback?.status == "APPROVED"
          ) {
            setHasFeedback(true);
          }
        })
        .catch(() => {
          toast.error("Erro ao recuperar as informações de feedback");
        });
    }
  }, [attendanceId, updateAttendance]);

  useEffect(() => {
    if (
      attendanceId &&
      permissions.includes("create:quality:attendance_review")
    ) {
      qualityApi
        .get(`/attendance-review/${attendanceId}`)
        .then((response) => response.data)
        .then((response) => {
          setHasReview(response.length !== 0);
        })
        .catch(() => {
          toast.error(
            "Erro ao recuperar informações de revisão do atendimento",
          );
        });
    }
  }, [attendanceId, updateAttendance]);

  useEffect(() => {
    setLoading(true);

    qualityApi
      .get(`/attendances/${attendanceId}`)
      .then((response) => response.data)
      .then((response) => {
        const prompts = response.prompts || [];
        const aIPrompt = prompts.filter((p) => p.type === "AI");
        const humanPrompt = prompts.filter((p) => p.type === "HUMAN");
        setAttendance(response ?? {});
        setAttendanceName(response?.attendance ?? "");
        setSegmentID(response?.segment?.id);
        setMessages(response.messages ?? []);
        setLibraryMatches(response.matches ?? []);

        if (
          response.scoreBlockResults &&
          response.scoreBlockResults.length > 0
        ) {
          const scoreBlockResults = response.scoreBlockResults;

          const enhancedAIPrompts = aIPrompt.map((prompt) => {
            let foundViaFallback = false;

            const relatedScoreBlocks = scoreBlockResults.filter((sbr) => {
              if (sbr.attendanceAnalysisIds?.includes(prompt.id)) {
                return true;
              }

              if (prompt.promptId && sbr.blockConfig) {
                const foundInConfig = sbr.blockConfig.some(
                  (config) => config.promptId === prompt.promptId,
                );
                if (foundInConfig) {
                  foundViaFallback = true;
                  console.warn(
                    `[ScoreBlock] AI Prompt ${prompt.id} encontrado via fallback (promptId: ${prompt.promptId}) - Bloco: ${sbr.ScoreBlockRulesTitle}`,
                  );
                }
                return foundInConfig;
              }

              return false;
            });

            if (relatedScoreBlocks.length === 0 && prompt.promptId) {
              console.info(
                `[ScoreBlock] Nenhum bloco encontrado para AI Prompt ${prompt.id} (promptId: ${prompt.promptId}, title: ${prompt.title})`,
              );
            }

            return {
              ...prompt,
              scoreBlockResults: relatedScoreBlocks,
            };
          });

          const enhancedHumanPrompts = humanPrompt.map((prompt) => {
            let foundViaFallback = false;

            const relatedScoreBlocks = scoreBlockResults.filter((sbr) => {
              if (sbr.attendanceAnalysisIds?.includes(prompt.id)) {
                return true;
              }
              if (prompt.evaluableEvent?.id && sbr.blockConfig) {
                const foundInConfig = sbr.blockConfig.some(
                  (config) =>
                    config.evaluableEventId === prompt.evaluableEvent.id,
                );
                if (foundInConfig) {
                  foundViaFallback = true;
                  console.warn(
                    `[ScoreBlock] Human Prompt ${prompt.id} encontrado via fallback (evaluableEventId: ${prompt.evaluableEvent.id}) - Bloco: ${sbr.ScoreBlockRulesTitle}`,
                  );
                }
                return foundInConfig;
              }

              return false;
            });

            if (relatedScoreBlocks.length === 0 && prompt.evaluableEvent?.id) {
              console.info(
                `[ScoreBlock] Nenhum bloco encontrado para Human Prompt ${prompt.id} (evaluableEventId: ${prompt.evaluableEvent.id}, title: ${prompt.evaluableEvent.title})`,
              );
            }

            return {
              ...prompt,
              scoreBlockResults: relatedScoreBlocks,
            };
          });

          setPrompts(enhancedAIPrompts);
          setEvaluatedHumanPrompt(enhancedHumanPrompts);
        } else {
          setPrompts(aIPrompt ?? []);
          setEvaluatedHumanPrompt(humanPrompt);
        }
        if (response?.client?.id) {
          qualityApi
            .get(
              `/sla/configuration?page=1&limit=1&clientId=${response.client.id}`,
            )
            .then((slaResponse) => {
              setSlaConfig(slaResponse?.data?.records[0]?.config);
            })
            .catch(() => {
              toast.error(
                "Erro ao recuperar configuração de Janela de monitoria!",
              );
            });
        }
        setLoading(false);
      })
      .catch((e) => {
        console.log(e);
        toast.error("Erro ao recuperar as análises do atendimento!");
        setLoading(false);
        return;
      });
  }, [attendanceId, updateAttendance]);

  useEffect(() => {
    qualityApi
      .get(`/evaluable-events?segmentId=${segmentID}&limit=10000`)
      .then((response) => response.data)
      .then((response) => {
        setEvaluableEvent(response.prompts ?? []);
      })
      .catch(() => {
        toast.error("Erro ao recuperar prompts não processáveis!");
        setLoading(false);
        return;
      });
  }, [segmentID, updateAttendance]);

  useEffect(() => {
    if (segmentID) {
      qualityApi
        .get(`/score/rule?page=1&limit=1000&segmentId=${segmentID}&active=true`)
        .then((response) => {
          const data = response?.data?.data;
          const promptsPriority = [];
          const promptsMap = {};

          data?.[0]?.scoreRules.forEach((item) => {
            if (
              item.promptId &&
              (!promptsMap[item.promptId] || item.priority)
            ) {
              promptsPriority.push({
                id: item.promptId,
                priority: item.priority,
              });
              promptsMap[item.promptId] = true;
            }
          });

          const evaluableEventPriority = [];
          const evaluableEventMap = {};
          setScoreRule(data?.[0]?.scoreRules);
          data?.[0]?.scoreRules.forEach((item) => {
            if (
              item.evaluableEventId &&
              (!evaluableEventMap[item.evaluableEventId.id] || item.priority)
            ) {
              evaluableEventPriority.push({
                id: item.evaluableEventId.id,
                priority: item.priority,
              });
              evaluableEventMap[item.evaluableEventId.id] = true;
            }
          });
          setEvaluableEventPriority(evaluableEventPriority);
          setScoreBlockRules(data?.[0]?.scoreBlockRules ?? []);
        })
        .catch(() => {
          toast.error("Erro ao recuperar as regras de prioridade!");
        });
    }
  }, [prompts]);

  const deleteAnalysis = (id) => {
    qualityApi
      .delete(`/attendances/analysis/${id}`)
      .then(() => {
        toast.success("Análise deletada com sucesso!");
        setUpdateAttendance(!updateAttendance);
      })
      .catch(() => {
        toast.error("Erro ao deletar análise!");
      });
  };

  useEffect(() => {
    if (
      prompts &&
      attendanceId &&
      permissions.includes("read:quality:contestation")
    ) {
      qualityApi
        .get(`/contestation/attendance/${attendanceId}`)
        .then((response) => {
          if (response?.data?.length) {
            setContestation(response.data);
          }
        })
        .catch(() => {
          toast.error("Erro ao recuperar contestações");
        });
    }
  }, [attendanceId, prompts]);

  const isContestationExpired =
    deadlineContestation && moment().isAfter(deadlineContestation);
  const isEvaluableEventExpired =
    deadlineEvaluableEvent && moment().isAfter(deadlineEvaluableEvent);

  const hasBlockConfiguration =
    prompts.some(
      (prompt) =>
        prompt.scoreBlockResults && prompt.scoreBlockResults.length > 0,
    ) ||
    evaluatedHumanPrompt.some(
      (event) => event.scoreBlockResults && event.scoreBlockResults.length > 0,
    );

  const hasBlockScore = (item) => {
    return item.scoreBlockResults && item.scoreBlockResults.length > 0;
  };

  const getBlockAnalysisIds = (targetItem) => {
    if (!hasBlockScore(targetItem)) {
      return [targetItem.id];
    }

    const blockId = targetItem.scoreBlockResults[0]?.scoreBlockRulesId;
    if (!blockId) {
      return [targetItem.id];
    }

    const allBlockItems = [];

    prompts.forEach((prompt) => {
      if (
        hasBlockScore(prompt) &&
        prompt.scoreBlockResults[0]?.scoreBlockRulesId === blockId
      ) {
        allBlockItems.push(prompt.id);
      }
    });

    evaluatedHumanPrompt.forEach((event) => {
      if (
        hasBlockScore(event) &&
        event.scoreBlockResults[0]?.scoreBlockRulesId === blockId
      ) {
        allBlockItems.push(event.id);
      }
    });

    return allBlockItems.length > 0 ? allBlockItems : [targetItem.id];
  };

  const getBlockContestationStatus = (targetItem) => {
    if (!hasBlockScore(targetItem)) {
      return contestation.find((item) => item.analysisId === targetItem.id);
    }

    const blockAnalysisIds = getBlockAnalysisIds(targetItem);

    return contestation.find((item) =>
      blockAnalysisIds.includes(item.analysisId),
    );
  };

  const canItemBeContested = (item) => {
    if (!hasBlockConfiguration) {
      return true;
    }

    return hasBlockScore(item);
  };

  const renderBlockRules = (scoreBlockResults) => {
    if (!scoreBlockResults || scoreBlockResults.length === 0) {
      return (
        <span className="text-muted" style={{ fontSize: "0.85em" }}>
          Nenhum bloco aplicado
        </span>
      );
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        {scoreBlockResults.map((block, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "4px 8px",
              backgroundColor: "#f8f9fa",
              borderRadius: "4px",
              border: `1px solid ${block.passed ? "#28a745" : "#dc3545"}`,
              borderLeft: `6px solid ${block.passed ? "#28a745" : "#dc3545"}`,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <span
              style={{
                fontSize: "0.85em",
                fontWeight: "500",
                color: block.passed ? "#28a745" : "#dc3545",
                display: "inline-block",
                minWidth: "100px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={`Bloco: ${block.blockTitle ? block?.blockTitle : block?.title} | Status: ${block.passed ? "Passou" : "Não passou"} | Nota: ${block.score}`}
            >
              {block.blockTitle ? block?.blockTitle : block?.title}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const getBlockMarkerColor = (scoreBlockResults) => {
    if (!scoreBlockResults || scoreBlockResults.length === 0) {
      return null;
    }

    return scoreBlockResults
      .map((block) => block.blockColor)
      .filter((color) => color !== null && color !== undefined);
  };

  // ...existing code...
  const getBlockMarkerClass = (scoreBlockResults) => {
    const colors = getBlockMarkerColor(scoreBlockResults);
    if (!colors || colors.length === 0) return "";

    // Distinguir entre uma cor e múltiplas cores
    if (colors.length === 1) {
      return "table-row-with-block-marker";
    } else {
      return "table-row-with-multiple-block-markers";
    }
  };

  const getBlockMarkerStyle = (scoreBlockResults) => {
    const colors = getBlockMarkerColor(scoreBlockResults);
    if (!colors || colors.length === 0) return {};

    if (colors.length === 1) {
      // Uma cor única - usar variável CSS simples
      return { "--block-color": colors[0] };
    }

    // Múltiplas cores - criar variáveis numeradas para cada cor
    const style = {};
    colors.forEach((color, index) => {
      style[`--block-color-${index}`] = color;
    });
    style["--block-count"] = colors.length;

    return style;
  };

  const blockMarkerStyles = `
  .table-row-with-block-marker {
    position: relative;
    border-left: 12px solid var(--block-color) !important;
  }

  .table-row-with-block-marker td {
    border-left: none !important;
  }

  .table-row-with-block-marker td:first-child {
    padding-left: 8px !important;
  }

  .table-row-with-multiple-block-markers {
    position: relative;
    border-left:  6px solid transparent !important;
  }

  .table-row-with-multiple-block-markers td {
    border-left: none !important;
  }

  .table-row-with-multiple-block-markers td:first-child {
    padding-left: 8px !important;
  }

  .table-row-with-multiple-block-markers::after {
    content: '';
    position: absolute;
    left: -12px;
    top: 0;
    bottom: 0;
    width: 12px;
    z-index: 1;
    pointer-events: none;
  }

  /* Estilos específicos baseados na quantidade de cores */
  .table-row-with-multiple-block-markers[style*="--block-count: 1"]::after {
    background: var(--block-color-0);
  }

  .table-row-with-multiple-block-markers[style*="--block-count: 2"]::after {
    background: linear-gradient(to bottom,
      var(--block-color-0) 0%,
      var(--block-color-0) 50%,
      var(--block-color-1) 50%,
      var(--block-color-1) 100%);
  }

  .table-row-with-multiple-block-markers[style*="--block-count: 3"]::after {
    background: linear-gradient(to bottom,
      var(--block-color-0) 0%,
      var(--block-color-0) 33.33%,
      var(--block-color-1) 33.33%,
      var(--block-color-1) 66.67%,
      var(--block-color-2) 66.67%,
      var(--block-color-2) 100%);
  }

  .table-row-with-multiple-block-markers[style*="--block-count: 4"]::after {
    background: linear-gradient(to bottom,
      var(--block-color-0) 0%,
      var(--block-color-0) 25%,
      var(--block-color-1) 25%,
      var(--block-color-1) 50%,
      var(--block-color-2) 50%,
      var(--block-color-2) 75%,
      var(--block-color-3) 75%,
      var(--block-color-3) 100%);
  }

  .table-row-with-multiple-block-markers[style*="--block-count: 5"]::after {
    background: linear-gradient(to bottom,
      var(--block-color-0) 0%,
      var(--block-color-0) 20%,
      var(--block-color-1) 20%,
      var(--block-color-1) 40%,
      var(--block-color-2) 40%,
      var(--block-color-2) 60%,
      var(--block-color-3) 60%,
      var(--block-color-3) 80%,
      var(--block-color-4) 80%,
      var(--block-color-4) 100%);
  }

  .styled-scroll table {
    table-layout: fixed !important;
  }
`;

  const getReceivedScore = (item, contestationData) => {
    if (item.scoreBlockResults && item.scoreBlockResults.length > 0) {
      const allBlocksPassed = item.scoreBlockResults.every(
        (block) => block.passed,
      );

      if (contestationData?.status === "REJECTED") {
        return <Close style={{ color: "red" }} />;
      }

      return allBlocksPassed ? (
        <CheckCircle color="success" />
      ) : (
        <Close style={{ color: "red" }} />
      );
    }

    if (!item.score || item.score.length === 0) {
      return contestationData?.status === "REJECTED" ? (
        <Close style={{ color: "red" }} />
      ) : (
        " "
      );
    }

    return item.score.every((rule) => rule.passed) ? (
      <CheckCircle color="success" />
    ) : (
      <Close style={{ color: "red" }} />
    );
  };

  return (
    <>
      <style>{blockMarkerStyles}</style>
      <Container className="mt-4" fluid style={{ marginBottom: "100px" }}>
        <Col className="" xl="12">
          <Card className="bg-secondary shadow">
            <CardHeader className="bg-white border-0">
              <Row className="align-items-center">
                <Col xs="8">
                  <h3 className="mb-0">
                    <b>Atendimento:</b>{" "}
                    <CopyableText label="Atendimento" text={attendanceName} />
                  </h3>
                </Col>
                <Col className="text-right" xs="4"></Col>
              </Row>
            </CardHeader>
            <CardBody>
              <div className="pl-lg-12">
                <Col className="pl-lg-12">
                  <FormGroup>
                    <label className="form-control-label">
                      <b>Áudio</b>
                    </label>
                    <div>
                      {!loading && (
                        <AudioPlayer
                          attendanceId={attendanceId}
                          filename={attendanceName}
                          source={attendance?.source}
                          folderId={attendance?.folderId}
                          audio={attendance?.audio}
                          origin={attendance?.origin}
                        />
                      )}
                    </div>
                  </FormGroup>
                </Col>
              </div>
              <div className="pl-lg-12">
                <Col className="pl-lg-12">
                  <FormGroup
                    style={{
                      backgroundColor: "aliceblue",
                    }}
                  >
                    <Accordion
                      defaultExpanded
                      sx={{
                        "&.Mui-expanded": {
                          paddingBottom: "12px",
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="info-content"
                        id="info-header"
                      >
                        <label
                          className="form-control-label"
                          style={{ marginBottom: 0 }}
                        >
                          <b>Informações do atendimento</b>
                        </label>
                      </AccordionSummary>
                      <Divider />
                      <Col className="pl-lg-12 pt-2">
                        <div>
                          <label className="form-control-label">
                            Segmento:{" "}
                            <span
                              style={{
                                fontWeight: 300,
                              }}
                            >
                              {attendance?.segment?.title ?? "N/A"}
                            </span>
                          </label>
                          <br />
                          <label className="form-control-label">
                            Processado por:{" "}
                            <span
                              style={{
                                fontWeight: 300,
                              }}
                            >
                              {attendance.user?.username ?? "N/A"}
                            </span>
                          </label>
                          <br />
                          <label className="form-control-label">
                            Supervisor:{" "}
                            <span
                              style={{
                                fontWeight: 300,
                              }}
                            >
                              {attendance.supervisorExternalId ?? "N/A"}
                            </span>
                          </label>
                          <br />
                          <label className="form-control-label">
                            Consultor:{" "}
                            <span
                              style={{
                                fontWeight: 300,
                              }}
                            >
                              {attendance.consultantExternalId ?? "N/A"}
                            </span>
                          </label>
                          <br />
                          <label className="form-control-label">
                            Nota do consultor (Total):{" "}
                            <span
                              style={{
                                fontWeight: 300,
                              }}
                            >
                              {attendance.totalScore ?? "N/A"}
                            </span>
                          </label>
                          <br />
                          {parseFloat(attendance.proportionalScore) > 0 && (
                            <>
                              <label className="form-control-label">
                                Nota do consultor (Proporcional):{" "}
                                <i
                                  className="fas fa-info-circle"
                                  id="proportionalScoreInfo"
                                />
                                <Tooltip
                                  placement="right"
                                  isOpen={tooltipOpen}
                                  target="proportionalScoreInfo"
                                  toggle={toggleTooltip}
                                >
                                  Nota baseada apenas nas avaliações recebidas
                                </Tooltip>{" "}
                                <span
                                  style={{
                                    fontWeight: 300,
                                  }}
                                >
                                  {attendance.proportionalScore ?? "N/A"}
                                </span>
                              </label>
                              <br />
                            </>
                          )}
                          <></>
                          {permissions?.includes(
                            "create:quality:attendance_review",
                          ) && (
                            <div
                              className="d-flex mb-1"
                              style={{
                                gap: "4px",
                                alignItems: "center",
                              }}
                            >
                              <label
                                className="form-control-label"
                                style={{
                                  margin: 0,
                                }}
                              >
                                Review:
                              </label>
                              <span
                                style={{
                                  fontWeight: 300,
                                }}
                              >
                                <ModalAttendanceReview
                                  title={hasReview ? "Revisado" : "Pendente"}
                                  disabled={hasReview}
                                  attendanceId={attendanceId}
                                  setUpdateAttendance={setUpdateAttendance}
                                  updateAttendance={updateAttendance}
                                />
                              </span>
                              <br />
                            </div>
                          )}
                          {permissions?.includes(
                            "create:quality:user_attendance_feedback",
                          ) && (
                            <>
                              <div
                                className="d-flex mb-1"
                                style={{
                                  gap: "4px",
                                  alignItems: "center",
                                }}
                              >
                                <label
                                  className="form-control-label"
                                  style={{
                                    margin: 0,
                                  }}
                                >
                                  Feedback:
                                </label>
                                <span
                                  style={{
                                    fontWeight: 300,
                                  }}
                                >
                                  <ModalUserAttendanceFeedback
                                    title={
                                      hasFeedback
                                        ? "Feedback enviado"
                                        : "Enviar feedback"
                                    }
                                    disabled={hasFeedback}
                                    consultant={
                                      attendance?.consultantExternalId
                                    }
                                    attendanceId={attendanceId}
                                    setUpdateAttendance={setUpdateAttendance}
                                    updateAttendance={updateAttendance}
                                    clientId={attendance?.client?.id}
                                  />
                                </span>
                                <br />
                              </div>
                              {feedback ? (
                                <>
                                  <div
                                    className="d-flex flex-column mb-1 pl-3"
                                    style={{
                                      gap: "4px",
                                      fontSize: "0.8em",
                                    }}
                                  >
                                    <label className="form-control-label">
                                      Status{" "}
                                      <span
                                        style={{
                                          fontWeight: 300,
                                          fontStyle: "italic",
                                        }}
                                      >
                                        {getFeedbackStatusLabel(
                                          feedback?.status,
                                        )}
                                      </span>
                                    </label>
                                    <label className="form-control-label">
                                      Consultor{" "}
                                      <span
                                        style={{
                                          fontWeight: 300,
                                          fontStyle: "italic",
                                        }}
                                      >
                                        {feedback?.consultant?.username}
                                      </span>
                                    </label>
                                    <label className="form-control-label">
                                      Registrado em{" "}
                                      <span
                                        style={{
                                          fontWeight: 300,
                                          fontStyle: "italic",
                                        }}
                                      >
                                        {moment(feedback?.createdAt).format(
                                          "DD/MM/YYYY HH:mm",
                                        )}
                                      </span>
                                    </label>
                                  </div>
                                </>
                              ) : (
                                ""
                              )}
                            </>
                          )}
                          {slaConfig ? (
                            <>
                              <label className="form-control-label">
                                Janela de Monitoria
                              </label>
                              <div
                                className="d-flex flex-column mb-1 pl-3"
                                style={{
                                  gap: "4px",
                                  fontSize: "0.8em",
                                }}
                              >
                                <label className="form-control-label">
                                  Contestação:&nbsp;
                                  {isContestationExpired ? (
                                    <span
                                      style={{
                                        color: "red",
                                        fontWeight: 300,
                                        fontStyle: "italic",
                                      }}
                                    >
                                      Expirado em{" "}
                                      {deadlineContestation &&
                                        deadlineContestation.format(
                                          "DD/MM/YYYY HH:mm",
                                        )}
                                    </span>
                                  ) : deadlineContestation ? (
                                    <>
                                      <span
                                        style={{
                                          color: "#007bff",
                                          fontWeight: 300,
                                          fontStyle: "italic",
                                        }}
                                      >
                                        {`Disponível até ${deadlineContestation.format("DD/MM/YYYY HH:mm")} `}
                                        <b style={{ fontStyle: "normal" }}>
                                          ({deadlineContestation.fromNow(true)}{" "}
                                          restantes)
                                        </b>
                                      </span>
                                    </>
                                  ) : (
                                    "N/A"
                                  )}
                                </label>
                                <label className="form-control-label">
                                  Evento Avaliável:&nbsp;
                                  {isEvaluableEventExpired ? (
                                    <span
                                      style={{
                                        color: "red",
                                        fontWeight: 300,
                                        fontStyle: "italic",
                                      }}
                                    >
                                      Expirado em{" "}
                                      {deadlineEvaluableEvent &&
                                        deadlineEvaluableEvent.format(
                                          "DD/MM/YYYY HH:mm",
                                        )}
                                    </span>
                                  ) : deadlineEvaluableEvent ? (
                                    <>
                                      <span
                                        style={{
                                          color: "#007bff",
                                          fontWeight: 300,
                                          fontStyle: "italic",
                                        }}
                                      >
                                        {`Disponível até ${deadlineEvaluableEvent.format("DD/MM/YYYY HH:mm")} `}
                                        <b style={{ fontStyle: "normal" }}>
                                          (
                                          {deadlineEvaluableEvent.fromNow(true)}{" "}
                                          restantes)
                                        </b>
                                      </span>
                                    </>
                                  ) : (
                                    "N/A"
                                  )}
                                </label>
                              </div>
                            </>
                          ) : (
                            ""
                          )}
                          {permissions?.includes(
                            "update:quality:contestation_status",
                          ) && (
                            <div
                              className="d-flex"
                              style={{
                                gap: "4px",
                                alignItems: "center",
                              }}
                            >
                              <label
                                className="form-control-label"
                                style={{
                                  margin: 0,
                                }}
                              >
                                Itens contestados resolvidos:
                              </label>
                              <span
                                style={{
                                  fontWeight: 300,
                                }}
                              >
                                {`${
                                  contestation.filter(
                                    (item) => item.status !== "PENDING",
                                  ).length
                                } / ${contestation.length}`}
                              </span>
                              <br />
                            </div>
                          )}
                        </div>
                      </Col>
                    </Accordion>
                  </FormGroup>
                </Col>
              </div>
              {/* metadata */}
              <div className="pl-lg-12 col-12 col-md-12 col-sm-12 mt-2 mb-2">
                <Accordion>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="metadata-content"
                    id="metadata-header"
                  >
                    <label
                      className="form-control-label"
                      style={{ marginBottom: 0 }}
                    >
                      <b>Metadados</b>{" "}
                      {attendance?.event?.metadata.length > 0 && (
                        <Badge
                          color="primary"
                          style={{ fontSize: "12px" }}
                          onClick={(e) => e.preventDefault()}
                        >
                          ({attendance?.event?.metadata.length})
                        </Badge>
                      )}
                    </label>
                  </AccordionSummary>
                  {Array.isArray(attendance?.event?.metadata) &&
                  attendance?.event?.metadata.length > 0 ? (
                    <AccordionDetails
                      className="styled-scroll d-flex flex-column justify-content-center align-items-start pl-3 pb-1"
                      style={{
                        overflowY: "auto",
                        color: "#525F7F",
                      }}
                    >
                      {attendance?.event?.metadata.map((metadata, _) => {
                        return (
                          <div
                            key={_}
                            className="d-flex flex-row justify-content-center align-items-start"
                          >
                            <label
                              className="text-capitalize"
                              style={{
                                fontSize: "0.87em",
                                fontWeight: "bold",
                                marginRight: "4px",
                              }}
                            >
                              {metadata.metaKey}:
                            </label>
                            <label style={{ fontSize: "0.87em" }}>
                              {metadata.metaValue}
                            </label>
                          </div>
                        );
                      })}
                    </AccordionDetails>
                  ) : (
                    <AccordionDetails className="d-flex flex-column justify-content-center align-items-start pl-2">
                      <label
                        className="text-muted"
                        style={{ fontSize: "0.9em", fontStyle: "italic" }}
                      >
                        Esse evento não possui metadados.
                      </label>
                    </AccordionDetails>
                  )}
                </Accordion>
              </div>
              {/* transcription */}
              <div className="pl-lg-12 mt-4">
                <Col className="pl-lg-12">
                  <FormGroup>
                    <Accordion>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="transcription-content"
                        id="transcription-header"
                      >
                        <label
                          className="form-control-label"
                          style={{ marginBottom: 0 }}
                        >
                          <b>Transcrição</b>{" "}
                          {messages.length > 0 && (
                            <Badge
                              color="primary"
                              style={{ fontSize: "12px" }}
                              onClick={(e) => e.preventDefault()}
                            >
                              ({messages.length})
                            </Badge>
                          )}
                        </label>
                      </AccordionSummary>
                      <AccordionDetails>
                        <div
                          className="styled-scroll"
                          style={styledTableScroll}
                        >
                          {loading ? <Loading /> : ""}
                          <Table
                            style={{
                              tableLayout: "fixed",
                              width: "100%",
                            }}
                          >
                            <thead className="thead-ligth"></thead>
                            <tbody>
                              {messages.map((message, i) => {
                                let rowStyle = {};

                                const escapeHtml = (text) => {
                                  return text
                                    .replace(/&/g, "&amp;")
                                    .replace(/</g, "&lt;")
                                    .replace(/>/g, "&gt;")
                                    .replace(/"/g, "&quot;")
                                    .replace(/'/g, "&#x27;");
                                };

                                let formattedMessage = escapeHtml(message);

                                if (message.match(/\b(OPERADOR|AGENT)\b/)) {
                                  rowStyle = { backgroundColor: "#e3f2fd" };
                                  formattedMessage = formattedMessage.replace(
                                    /(OPERADOR|AGENT)/g,
                                    function (match) {
                                      return "<b>" + match + "</b>";
                                    },
                                  );
                                } else if (
                                  message.match(/\b(CLIENTE|CLIENT)\b/)
                                ) {
                                  rowStyle = { backgroundColor: "#fff" };
                                  formattedMessage = formattedMessage.replace(
                                    /(CLIENTE|CLIENT)/g,
                                    function (match) {
                                      return "<b>" + match + "</b>";
                                    },
                                  );
                                }

                                formattedMessage = formattedMessage.replace(
                                  /&lt;([^&]+)&gt;/g,
                                  function (match, content) {
                                    return "<b>&lt;" + content + "&gt;</b>";
                                  },
                                );

                                return (
                                  <tr key={i} style={rowStyle}>
                                    <td>
                                      <div
                                        style={{
                                          width: "100%",
                                          whiteSpace: "pre-line",
                                          wordWrap: "break-word",
                                          overflowWrap: "break-word",
                                        }}
                                        dangerouslySetInnerHTML={{
                                          __html: formattedMessage,
                                        }}
                                      />
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  </FormGroup>
                </Col>
              </div>
              <Col className="pl-lg-12">
                {/* prompts */}
                <Accordion>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="analysis-content"
                    id="analysis-header"
                  >
                    <label
                      className="form-control-label"
                      style={{ marginBottom: 0 }}
                    >
                      <b>Análises Generativas</b>{" "}
                      {prompts.length > 0 && (
                        <Badge
                          color="primary"
                          style={{ fontSize: "12px" }}
                          onClick={(e) => e.preventDefault()}
                        >
                          ({prompts.length})
                        </Badge>
                      )}
                    </label>
                  </AccordionSummary>
                  <div className="styled-scroll" style={styledTableScroll}>
                    {loading ? <Loading /> : ""}
                    <Table
                      style={{
                        tableLayout: "fixed",
                        width: "100%",
                      }}
                    >
                      <thead className="thead-ligth">
                        <tr className="custom-head">
                          <th
                            scope="col"
                            title="Prompt"
                            onClick={() =>
                              setOrderPromptsBy([
                                "prompt",
                                orderPromptsBy[1] === "desc" ? "asc" : "desc",
                              ])
                            }
                            style={tableHeaderStyle}
                          >
                            Prompt{" "}
                            {orderPromptsBy.length &&
                            orderPromptsBy[0] === "prompt"
                              ? orderPromptsBy[1] === "desc"
                                ? "🠋"
                                : "🠉"
                              : ""}
                          </th>
                          <th
                            scope="col"
                            title="Resposta"
                            onClick={() =>
                              setOrderPromptsBy([
                                "response",
                                orderPromptsBy[1] === "desc" ? "asc" : "desc",
                              ])
                            }
                            style={tableHeaderStyle}
                          >
                            Resposta{" "}
                            {orderPromptsBy.length &&
                            orderPromptsBy[0] === "response"
                              ? orderPromptsBy[1] === "desc"
                                ? "🠋"
                                : "🠉"
                              : ""}
                          </th>
                          <th
                            scope="col"
                            title="Checklist"
                            onClick={() =>
                              setOrderPromptsBy([
                                "checklist",
                                orderPromptsBy[1] === "desc" ? "asc" : "desc",
                              ])
                            }
                            style={tableHeaderStyle}
                          >
                            Checklist{" "}
                            {orderPromptsBy.length &&
                            orderPromptsBy[0] === "checklist"
                              ? orderPromptsBy[1] === "desc"
                                ? "🠋"
                                : "🠉"
                              : ""}
                          </th>
                          <th
                            scope="col"
                            title="Pesos aplicados"
                            style={tableHeaderStyle}
                          >
                            Pesos aplicados
                          </th>
                          <th scope="col" style={tableHeaderStyleCenter}>
                            Nota recebida
                          </th>
                          <th
                            scope="col"
                            title="Horário de análise"
                            onClick={() =>
                              setOrderPromptsBy([
                                "date",
                                orderPromptsBy[1] === "desc" ? "asc" : "desc",
                              ])
                            }
                            style={tableHeaderStyle}
                          >
                            Horário de análise{" "}
                            {orderPromptsBy.length &&
                            orderPromptsBy[0] === "date"
                              ? orderPromptsBy[1] === "desc"
                                ? "🠋"
                                : "🠉"
                              : ""}
                          </th>
                          <th
                            scope="col"
                            title="Classificação"
                            style={tableHeaderStyleCenter}
                          >
                            Classificação
                          </th>
                          <th
                            scope="col"
                            title="Bloco de Regras"
                            style={tableHeaderStyle}
                          >
                            Bloco de Regras
                          </th>
                          {permissions?.includes(
                            "read:quality:contestation",
                          ) && (
                            <th
                              scope="col"
                              title="Contestação"
                              style={tableHeaderStyleCenter}
                            >
                              Contestação
                            </th>
                          )}
                          {permissions?.includes(
                            "update:quality:contestation_status",
                          ) && (
                            <th
                              scope="col"
                              title="Regras de score"
                              style={tableHeaderStyle}
                            >
                              Regras de score
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {prompts
                          .sort((a, b) => {
                            if (orderPromptsBy.length) {
                              let firstData = a;
                              let secondData = b;

                              switch (orderPromptsBy[0]) {
                                case "type":
                                  firstData = a.type;
                                  secondData = b.type;
                                  break;
                                case "prompt":
                                  firstData = a.prompt;
                                  secondData = b.prompt;
                                  break;
                                case "response":
                                  firstData = a.response;
                                  secondData = b.response;
                                  break;
                                case "checklist":
                                  firstData = a.checklist;
                                  secondData = b.checklist;
                                  break;
                                case "price":
                                  firstData = (a.price * 10000).toString();
                                  secondData = (b.price * 10000).toString();
                                  break;
                              }

                              if (orderPromptsBy[0] === "date") {
                                firstData = new Date(a.createdAt);
                                secondData = new Date(b.createdAt);
                                if (orderPromptsBy[1] === "desc") {
                                  return firstData - secondData;
                                }
                                return secondData - firstData;
                              }

                              const questionA = firstData?.replace(
                                questionRegex,
                                "$1",
                              );

                              const questionB = secondData?.replace(
                                questionRegex,
                                "$1",
                              );

                              let numA = questionA.match(/\d+/g);
                              let numB = questionB.match(/\d+/g);

                              numA = numA ? parseInt(numA[0], 10) : 0;
                              numB = numB ? parseInt(numB[0], 10) : 0;

                              if (orderPromptsBy[1] === "desc") {
                                if (numA === numB) {
                                  return questionA.localeCompare(questionB);
                                } else {
                                  return numA - numB;
                                }
                              }
                              if (numA === numB) {
                                return questionB.localeCompare(questionA);
                              } else {
                                return numB - numA;
                              }
                            }

                            return true;
                          })
                          .map((prompt, i) => {
                            let priority =
                              prompt?.score?.some((item) => item.priority) ||
                              false;
                            const contestationData = contestation.find(
                              (item) => item.analysisId === prompt.id,
                            );

                            let analysisRules;
                            if (scoreBlockRules.length > 0) {
                              const matchingBlocks = scoreBlockRules.filter(
                                (block) => {
                                  return (
                                    block.promptsConfig &&
                                    block.promptsConfig.some(
                                      (p) => p.promptId === prompt.promptId,
                                    )
                                  );
                                },
                              );
                              priority = matchingBlocks.some(
                                (item) => item.priority,
                              );
                              analysisRules = [];
                              matchingBlocks.forEach((block) => {
                                const combinedBlockConfig = [
                                  ...(block.promptsConfig?.map((config) => ({
                                    ...config,
                                    type: "prompt",
                                  })) || []),
                                  ...(block.evaluableEventsConfig?.map(
                                    (config) => ({
                                      ...config,
                                      type: "evaluableEvent",
                                    }),
                                  ) || []),
                                  ...(block.librariesConfig?.map((config) => ({
                                    ...config,
                                    type: "library",
                                  })) || []),
                                ];

                                analysisRules.push({
                                  ...block,
                                  blockConfig: combinedBlockConfig,
                                });
                              });
                            } else {
                              analysisRules = scoreRule?.filter(
                                (item) => item.promptId === prompt.promptId,
                              );
                            }
                            return (
                              <tr
                                key={i}
                                className={`custom-tr ${getBlockMarkerClass(prompt.scoreBlockResults)}`}
                                style={getBlockMarkerStyle(
                                  prompt.scoreBlockResults,
                                )}
                              >
                                <td
                                  style={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  <ModalAttendanceAnalysisPrompt
                                    id={i}
                                    question={prompt.prompt}
                                    title={prompt.title}
                                    prompt={prompt.prompt}
                                  />
                                </td>
                                <td
                                  style={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  <ModalAttendanceAnalysisResponse
                                    response={prompt.response}
                                  />
                                </td>
                                <td>{prompt.checklist}</td>
                                <td
                                  style={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  <ModalPromptScoreDetails
                                    scoreRule={
                                      getBlockScoreRules(
                                        prompt.scoreBlockResults,
                                        prompt.promptId,
                                      ) || prompt.score
                                    }
                                  />
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  {getReceivedScore(prompt, contestationData)}
                                </td>
                                <td
                                  style={tableContentStyle}
                                  title={DateTool.format(prompt.createdAt)}
                                >
                                  {DateTool.format(prompt.createdAt)}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  {priority ? (
                                    <Flag
                                      sx={{ color: "red", fontSize: "28px" }}
                                    />
                                  ) : (
                                    <Flag
                                      sx={{
                                        color: "#cbcb0e",
                                        fontSize: "28px",
                                      }}
                                    />
                                  )}
                                </td>
                                <td>
                                  {renderBlockRules(prompt.scoreBlockResults)}
                                </td>
                                <td
                                  style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                  }}
                                >
                                  {permissions?.includes(
                                    "read:quality:contestation",
                                  ) &&
                                    (() => {
                                      const contestationData =
                                        getBlockContestationStatus(prompt);

                                      return contestationData ? (
                                        <ModalContestationStatus
                                          contestation={contestationData}
                                          updateAttendance={updateAttendance}
                                          scoreRule={analysisRules}
                                          setUpdateAttendance={
                                            setUpdateAttendance
                                          }
                                          isBlockScore={hasBlockScore(prompt)}
                                          blockAnalysisIds={
                                            hasBlockScore(prompt)
                                              ? getBlockAnalysisIds(prompt)
                                              : undefined
                                          }
                                        />
                                      ) : (
                                        permissions.includes(
                                          "create:quality:contestation",
                                        ) &&
                                          canItemBeContested(prompt) &&
                                          (isContestationExpired ? (
                                            <span
                                              style={{
                                                color: "red",
                                                fontWeight: 500,
                                              }}
                                            >
                                              Prazo expirado
                                            </span>
                                          ) : !canItemBeContested(prompt) ? (
                                            <span
                                              style={{
                                                color: "#666",
                                                fontWeight: 500,
                                                fontSize: "0.8em",
                                              }}
                                              title="Este item não faz parte de nenhum bloco de regras. Apenas itens que fazem parte de blocos podem ser contestados quando há configuração de blocos no segmento."
                                            >
                                              Não contestável
                                            </span>
                                          ) : (
                                            <ModalContestationForm
                                              attendanceId={attendance.id}
                                              analysisId={prompt.id}
                                              segmentId={
                                                attendance?.segment?.id
                                              }
                                              attendanceName={attendanceName}
                                              response={prompt.response}
                                              updateAttendance={
                                                updateAttendance
                                              }
                                              setUpdateAttendance={
                                                setUpdateAttendance
                                              }
                                              isBlockScore={hasBlockScore(
                                                prompt,
                                              )}
                                              scoreBlockIds={
                                                hasBlockScore(prompt)
                                                  ? getBlockAnalysisIds(prompt)
                                                  : undefined
                                              }
                                            />
                                          ))
                                      );
                                    })()}
                                </td>
                                {permissions?.includes(
                                  "update:quality:contestation_status",
                                ) && (
                                  <td>
                                    {scoreBlockRules.length > 0 ? (
                                      <ModalBlockRulesDetails
                                        scoreBlockResults={analysisRules}
                                        segmentId={segmentID}
                                        promptsData={prompts}
                                        evaluableEventsData={evaluableEvent}
                                        librariesData={libraryMatches}
                                      />
                                    ) : (
                                      <ModalPromptRulesDetails
                                        scoreRule={analysisRules}
                                      />
                                    )}
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                      </tbody>
                    </Table>
                  </div>
                </Accordion>
                <div className="mt-4">
                  {/* evaluable events */}
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="evaluable-event-content"
                      id="evaluable-event"
                    >
                      <label
                        className="form-control-label"
                        style={{
                          marginBottom: 0,
                        }}
                      >
                        <b>Eventos Avaliáveis</b>{" "}
                        {evaluableEvent.length > 0 && (
                          <Badge
                            color="primary"
                            style={{ fontSize: "12px" }}
                            onClick={(e) => e.preventDefault()}
                          >
                            ({evaluableEvent.length})
                          </Badge>
                        )}
                      </label>
                    </AccordionSummary>
                    <div className="styled-scroll" style={styledTableScroll}>
                      {loading ? <Loading /> : ""}
                      <Table
                        style={{
                          tableLayout: "fixed",
                          width: "100%",
                        }}
                      >
                        <thead className="thead-ligth">
                          <tr className="custom-head">
                            <th
                              scope="col"
                              title="Evento"
                              onClick={() =>
                                setOrderEvaluableEventsBy([
                                  "event",
                                  orderEvaluableEventsBy[1] === "desc"
                                    ? "asc"
                                    : "desc",
                                ])
                              }
                              style={tableHeaderStyle}
                            >
                              Evento{" "}
                              {orderEvaluableEventsBy.length &&
                              orderEvaluableEventsBy[0] === "event"
                                ? orderEvaluableEventsBy[1] === "desc"
                                  ? "🠋"
                                  : "🠉"
                                : ""}
                            </th>
                            <th
                              scope="col"
                              title="Resposta"
                              style={tableHeaderStyle}
                            >
                              Resposta
                            </th>
                            <th
                              scope="col"
                              title="Checklist"
                              style={tableHeaderStyle}
                            >
                              Checklist
                            </th>
                            <th
                              scope="col"
                              title="Pesos aplicados"
                              style={tableHeaderStyle}
                            >
                              Pesos aplicados
                            </th>
                            <th
                              scope="col"
                              title="Nota recebida"
                              style={tableHeaderStyleCenter}
                            >
                              Nota recebida
                            </th>
                            <th
                              scope="col"
                              title="Horário de análise"
                              style={tableHeaderStyle}
                            >
                              Horário de análise
                            </th>
                            <th
                              scope="col"
                              title="Classificação"
                              style={tableHeaderStyleCenter}
                            >
                              Classificação
                            </th>
                            <th
                              scope="col"
                              title="Bloco de Regras"
                              style={tableHeaderStyle}
                            >
                              Bloco de Regras
                            </th>
                            {permissions?.includes(
                              "read:quality:contestation",
                            ) && (
                              <th
                                scope="col"
                                title="Contestação"
                                style={tableHeaderStyleCenter}
                              >
                                Contestação
                              </th>
                            )}
                            {permissions?.includes(
                              "update:quality:contestation_status",
                            ) && (
                              <th
                                scope="col"
                                title="Regras de score"
                                style={tableHeaderStyle}
                              >
                                Regras de score
                              </th>
                            )}
                            <th
                              scope="col"
                              title="Ações"
                              style={tableHeaderStyleCenter}
                            >
                              Ações
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {evaluableEvent
                            .sort((a, b) => {
                              if (orderEvaluableEventsBy.length) {
                                const firstData = a.prompt || "";
                                const secondData = b.prompt || "";

                                if (orderEvaluableEventsBy[1] === "desc") {
                                  return secondData.localeCompare(firstData);
                                }
                                return firstData.localeCompare(secondData);
                              }

                              return 0;
                            })
                            .map((nprompt) => {
                              let promptElement = nprompt;

                              if (
                                evaluatedHumanPrompt.find(
                                  (p) => p.evaluableEvent?.id === nprompt.id,
                                )
                              ) {
                                promptElement = evaluatedHumanPrompt.find(
                                  (p) => p.evaluableEvent?.id === nprompt.id,
                                );
                                promptElement.evaluated = true;
                              }

                              let priority = evaluableEventPriority.find(
                                (item) =>
                                  item.id === nprompt?.id && item.priority,
                              );

                              const contestationData =
                                getBlockContestationStatus(promptElement);

                              let analysisRules;

                              if (scoreBlockRules.length > 0) {
                                const matchingBlocks = scoreBlockRules.filter(
                                  (block) => {
                                    return (
                                      block.evaluableEventsConfig &&
                                      block.evaluableEventsConfig.some(
                                        (ee) =>
                                          ee.evaluableEventId === nprompt.id,
                                      )
                                    );
                                  },
                                );
                                analysisRules = [];
                                priority =
                                  matchingBlocks?.some(
                                    (block) => block.priority,
                                  ) || false;

                                matchingBlocks.forEach((block) => {
                                  const combinedBlockConfig = [
                                    ...(block.promptsConfig?.map((config) => ({
                                      ...config,
                                      type: "prompt",
                                    })) || []),
                                    ...(block.evaluableEventsConfig?.map(
                                      (config) => ({
                                        ...config,
                                        type: "evaluableEvent",
                                      }),
                                    ) || []),
                                    ...(block.librariesConfig?.map(
                                      (config) => ({
                                        ...config,
                                        type: "library",
                                      }),
                                    ) || []),
                                  ];

                                  analysisRules.push({
                                    ...block,
                                    blockConfig: combinedBlockConfig,
                                  });
                                });
                              } else {
                                analysisRules = scoreRule?.filter(
                                  (item) =>
                                    item?.evaluableEventId?.id === nprompt.id,
                                );
                              }

                              return (
                                <tr
                                  key={promptElement.id}
                                  className={`custom-tr ${getBlockMarkerClass(promptElement.scoreBlockResults)}`}
                                  style={getBlockMarkerStyle(
                                    promptElement.scoreBlockResults,
                                  )}
                                >
                                  <td>
                                    <ModalAttendanceAnalysisPrompt
                                      id={promptElement.id}
                                      question={promptElement.prompt}
                                      title={promptElement.title}
                                      prompt={promptElement.prompt}
                                    />
                                  </td>
                                  <td>
                                    <ModalAttendanceAnalysisResponse
                                      response={promptElement.response}
                                    />
                                  </td>
                                  <td>
                                    <div style={{ width: "20px" }}>
                                      {promptElement.checklist}
                                    </div>
                                  </td>
                                  <td>
                                    <ModalPromptScoreDetails
                                      scoreRule={
                                        getBlockScoreRules(
                                          promptElement.scoreBlockResults,
                                          null,
                                          promptElement.evaluableEvent?.id,
                                        ) || promptElement.score
                                      }
                                    />
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    {getReceivedScore(
                                      promptElement,
                                      contestationData,
                                    )}
                                  </td>
                                  <td
                                    style={tableContentStyle}
                                    title={DateTool.format(
                                      promptElement.createdAt,
                                    )}
                                  >
                                    {promptElement.evaluated
                                      ? DateTool.format(promptElement.createdAt)
                                      : ""}
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    {priority ? (
                                      <Flag
                                        sx={{ color: "red", fontSize: "28px" }}
                                      />
                                    ) : (
                                      <Flag
                                        sx={{
                                          color: "#cbcb0e",
                                          fontSize: "28px",
                                        }}
                                      />
                                    )}
                                  </td>
                                  <td>
                                    {renderBlockRules(
                                      promptElement.scoreBlockResults,
                                    )}
                                  </td>
                                  <td
                                    style={{
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                    }}
                                  >
                                    {permissions?.includes(
                                      "read:quality:contestation",
                                    ) &&
                                      (contestationData ? (
                                        <ModalContestationStatus
                                          contestation={contestationData}
                                          updateAttendance={updateAttendance}
                                          scoreRule={analysisRules}
                                          setUpdateAttendance={
                                            setUpdateAttendance
                                          }
                                          evaluableEventId={
                                            promptElement?.evaluableEvent?.id
                                          }
                                          analysisId={promptElement?.id}
                                          clientId={attendance.client?.id}
                                          isBlockScore={hasBlockScore(
                                            promptElement,
                                          )}
                                          blockAnalysisIds={
                                            hasBlockScore(promptElement)
                                              ? getBlockAnalysisIds(
                                                  promptElement,
                                                )
                                              : undefined
                                          }
                                        />
                                      ) : (
                                        permissions.includes(
                                          "create:quality:contestation",
                                        ) &&
                                        (isContestationExpired ? (
                                          <span
                                            style={{
                                              color: "red",
                                              fontWeight: 500,
                                            }}
                                          >
                                            Prazo expirado
                                          </span>
                                        ) : !canItemBeContested(
                                            promptElement,
                                          ) ? (
                                          <span
                                            style={{
                                              color: "#666",
                                              fontWeight: 500,
                                              fontSize: "0.8em",
                                            }}
                                            title="Este item não faz parte de nenhum bloco de regras. Apenas itens que fazem parte de blocos podem ser contestados quando há configuração de blocos no segmento."
                                          >
                                            Não contestável
                                          </span>
                                        ) : (
                                          <ModalContestationForm
                                            attendanceId={attendance.id}
                                            analysisId={promptElement?.id}
                                            segmentId={attendance?.segment?.id}
                                            attendanceName={attendanceName}
                                            response={promptElement?.response}
                                            updateAttendance={updateAttendance}
                                            setUpdateAttendance={
                                              setUpdateAttendance
                                            }
                                            isBlockScore={hasBlockScore(
                                              promptElement,
                                            )}
                                            scoreBlockIds={
                                              hasBlockScore(promptElement)
                                                ? getBlockAnalysisIds(
                                                    promptElement,
                                                  )
                                                : undefined
                                            }
                                          />
                                        ))
                                      ))}
                                  </td>
                                  {permissions?.includes(
                                    "update:quality:contestation_status",
                                  ) && (
                                    <td>
                                      {scoreBlockRules.length > 0 ? (
                                        <ModalBlockRulesDetails
                                          scoreBlockResults={analysisRules}
                                          segmentId={segmentID}
                                          promptsData={prompts}
                                          evaluableEventsData={evaluableEvent}
                                          librariesData={libraryMatches}
                                        />
                                      ) : (
                                        <ModalPromptRulesDetails
                                          scoreRule={analysisRules}
                                        />
                                      )}
                                    </td>
                                  )}
                                  {promptElement.evaluated ? (
                                    <td>
                                      {isEvaluableEventExpired ? (
                                        <span
                                          style={{
                                            color: "red",
                                            fontWeight: 500,
                                          }}
                                        >
                                          Prazo expirado
                                        </span>
                                      ) : (
                                        <Row
                                          style={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                          }}
                                        >
                                          <ModalCustomAttendanceAnalysis
                                            attendanceId={attendanceId}
                                            analysisId={promptElement.id}
                                            segmentId={attendance?.segment?.id}
                                            promptId={
                                              promptElement.evaluableEvent.id
                                            }
                                            evaluated={true}
                                            update={updateAttendance}
                                            setUpdate={setUpdateAttendance}
                                          />
                                          <RemoveCircle
                                            type="button"
                                            sx={{ marginLeft: "16px" }}
                                            onClick={() =>
                                              deleteAnalysis(promptElement.id)
                                            }
                                            style={{ color: "red" }}
                                          />
                                        </Row>
                                      )}
                                    </td>
                                  ) : (
                                    <td
                                      style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                      }}
                                    >
                                      {isEvaluableEventExpired ? (
                                        <span
                                          style={{
                                            color: "red",
                                            fontWeight: 500,
                                          }}
                                        >
                                          Prazo expirado
                                        </span>
                                      ) : (
                                        <ModalCustomAttendanceAnalysis
                                          attendanceId={attendanceId}
                                          segmentId={attendance?.segment?.id}
                                          promptId={promptElement.id}
                                          evaluated={false}
                                          update={updateAttendance}
                                          setUpdate={setUpdateAttendance}
                                        />
                                      )}
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                        </tbody>
                      </Table>
                    </div>
                  </Accordion>
                </div>
                <div className="mt-4">
                  {/* library matches */}
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="matching-content"
                      id="matching-header"
                    >
                      <label
                        className="form-control-label"
                        style={{ marginBottom: 0 }}
                      >
                        <b>Enquadramento De Bibliotecas</b>{" "}
                        {libraryMatches.length > 0 && (
                          <Badge
                            color="primary"
                            style={{ fontSize: "12px" }}
                            onClick={(e) => e.preventDefault()}
                          >
                            ({libraryMatches.length})
                          </Badge>
                        )}
                      </label>
                    </AccordionSummary>
                    <Divider />
                    <div className="styled-scroll" style={styledTableScroll}>
                      {loading ? <Loading /> : ""}
                      <Table
                        style={{
                          tableLayout: "fixed",
                          width: "100%",
                        }}
                      >
                        <thead className="thead-ligth">
                          <tr className="custom-head">
                            <th
                              scope="col"
                              title="Biblioteca"
                              style={tableHeaderStyle}
                            >
                              Biblioteca
                            </th>
                            <th
                              scope="col"
                              title="Tipo"
                              style={tableHeaderStyle}
                            >
                              Tipo
                            </th>
                            <th
                              scope="col"
                              title="Enquadramentos"
                              style={tableHeaderStyleCenter}
                            >
                              Enquadramentos
                            </th>
                            <th
                              scope="col"
                              title="Nota recebida"
                              style={tableHeaderStyleCenter}
                            >
                              Nota recebida
                            </th>
                            <th
                              scope="col"
                              title="Ações"
                              style={tableHeaderStyleCenter}
                            >
                              Detalhes
                            </th>
                            {permissions?.includes(
                              "read:quality:contestation",
                            ) && (
                              <th
                                scope="col"
                                title="Contestação"
                                style={tableHeaderStyleCenter}
                              >
                                Contestação
                              </th>
                            )}
                            <th
                              scope="col"
                              title="Regras de score"
                              style={tableHeaderStyleCenter}
                            >
                              Regras de score
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const groupedMatches = libraryMatches.reduce(
                              (acc, match) => {
                                const key = `${match.libraryId}-${match.matchType}`;
                                if (!acc[key]) {
                                  acc[key] = {
                                    libraryId: match.libraryId,
                                    libraryTitle: match.libraryTitle,
                                    matchType: match.matchType,
                                    matches: [],
                                    totalTermsCount: 0,
                                  };
                                }
                                acc[key].matches.push(match);
                                acc[key].totalTermsCount += match.totalTerms;
                                return acc;
                              },
                              {},
                            );

                            return Object.values(groupedMatches).map(
                              (group, i) => (
                                <tr
                                  key={i}
                                  className={`custom-tr ${getBlockMarkerClass(group.matches[0].scoreBlockResults)}`}
                                  style={getBlockMarkerStyle(
                                    group.matches[0].scoreBlockResults,
                                  )}
                                >
                                  <td
                                    title={group.libraryTitle}
                                    style={tableContentStyle}
                                  >
                                    {group.libraryTitle}
                                  </td>
                                  <td>{libraryMatchType[group.matchType]}</td>
                                  <td style={{ textAlign: "center" }}>
                                    {group.totalTermsCount}
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    {(() => {
                                      const allScores = group.matches.flatMap(
                                        (match) => match.scores || [],
                                      );
                                      const hasScores = allScores.length > 0;
                                      const allPassed =
                                        allScores.length > 0 &&
                                        allScores.every(
                                          (score) => score.passed,
                                        );

                                      if (!hasScores) {
                                        return (
                                          <span className="text-muted">
                                            Sem avaliação
                                          </span>
                                        );
                                      }

                                      return allPassed ? (
                                        <CheckCircle color="success" />
                                      ) : (
                                        <Close style={{ color: "red" }} />
                                      );
                                    })()}
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    <div
                                      data-library-group={`${group.libraryId}-${group.matchType}`}
                                    >
                                      <ModalLibraryMatchDetails
                                        libraryTitle={group.libraryTitle}
                                        matchType={
                                          libraryMatchType[group.matchType]
                                        }
                                        matches={group.matches}
                                        attendance={attendance}
                                        attendanceName={attendanceName}
                                        contestation={contestation}
                                        updateAttendance={updateAttendance}
                                        setUpdateAttendance={
                                          setUpdateAttendance
                                        }
                                        scoreRule={scoreRule}
                                        permissions={permissions}
                                        isContestationExpired={
                                          isContestationExpired
                                        }
                                      />
                                    </div>
                                  </td>
                                  {permissions?.includes(
                                    "read:quality:contestation",
                                  ) && (
                                    <td
                                      style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                      }}
                                    >
                                      {(() => {
                                        const hasContestation =
                                          group.matches.some((match) =>
                                            contestation.find(
                                              (item) =>
                                                item.attendanceLibraryMatchId ===
                                                match.id?.toString(),
                                            ),
                                          );

                                        const groupContestationStatuses =
                                          group.matches
                                            .map(
                                              (match) =>
                                                contestation.find(
                                                  (item) =>
                                                    item.attendanceLibraryMatchId ===
                                                    match.id?.toString(),
                                                )?.status,
                                            )
                                            .filter(Boolean);

                                        let contestationStatus = null;
                                        const hasPending =
                                          groupContestationStatuses.includes(
                                            "PENDING",
                                          );
                                        const hasApproved =
                                          groupContestationStatuses.includes(
                                            "APPROVED",
                                          );
                                        const hasRejected =
                                          groupContestationStatuses.includes(
                                            "REJECTED",
                                          );

                                        if (
                                          (hasApproved && hasRejected) ||
                                          (hasApproved &&
                                            hasRejected &&
                                            hasPending)
                                        ) {
                                          contestationStatus = "MIXED";
                                        } else if (hasPending) {
                                          contestationStatus = "PENDING";
                                        } else if (hasApproved) {
                                          contestationStatus = "APPROVED";
                                        } else if (hasRejected) {
                                          contestationStatus = "REJECTED";
                                        }

                                        if (hasContestation) {
                                          return (
                                            <div
                                              style={{
                                                padding: "4px",
                                                borderRadius: "4px",
                                                marginLeft: "2px",
                                                backgroundColor: "#525f7f",
                                                cursor: "pointer",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                              }}
                                              onClick={() => {
                                                const modalButton =
                                                  document.querySelector(
                                                    `[data-library-group="${group.libraryId}-${group.matchType}"] button`,
                                                  );
                                                if (modalButton) {
                                                  modalButton.click();
                                                }
                                              }}
                                              title="Ver contestações nos detalhes"
                                            >
                                              {contestationStatus ===
                                              "APPROVED" ? (
                                                <Check
                                                  sx={{
                                                    fontSize: "20px",
                                                    color: "#90EE90",
                                                  }}
                                                />
                                              ) : contestationStatus ===
                                                "REJECTED" ? (
                                                <Close
                                                  sx={{
                                                    fontSize: "20px",
                                                    color: "red",
                                                  }}
                                                />
                                              ) : contestationStatus ===
                                                "MIXED" ? (
                                                <IndeterminateCheckBox
                                                  sx={{ color: "#ff9800" }}
                                                />
                                              ) : (
                                                <WatchLater
                                                  sx={{
                                                    fontSize: "20px",
                                                    color: "#cbcb0e",
                                                  }}
                                                />
                                              )}
                                            </div>
                                          );
                                        } else if (
                                          permissions.includes(
                                            "create:quality:contestation",
                                          )
                                        ) {
                                          if (isContestationExpired) {
                                            return (
                                              <span
                                                style={{
                                                  color: "red",
                                                  fontWeight: 500,
                                                }}
                                              >
                                                Prazo expirado
                                              </span>
                                            );
                                          } else {
                                            return (
                                              <AddBox
                                                type="button"
                                                color="inherit"
                                                fontSize="large"
                                                onClick={() => {
                                                  const modalButton =
                                                    document.querySelector(
                                                      `[data-library-group="${group.libraryId}-${group.matchType}"] button`,
                                                    );
                                                  if (modalButton) {
                                                    modalButton.click();
                                                    toast.info(
                                                      "Selecione o enquadramento para contestar.",
                                                    );
                                                  }
                                                }}
                                                title="Abrir detalhes para contestar"
                                                sx={{ cursor: "pointer" }}
                                              />
                                            );
                                          }
                                        }
                                        return null;
                                      })()}
                                    </td>
                                  )}
                                  <td style={{ textAlign: "center" }}>
                                    {(() => {
                                      let libraryRules;

                                      if (scoreBlockRules.length > 0) {
                                        const matchingBlocks =
                                          scoreBlockRules.filter((block) => {
                                            return (
                                              block.librariesConfig &&
                                              block.librariesConfig.some(
                                                (lib) =>
                                                  lib.libraryId ===
                                                  group.libraryId,
                                              )
                                            );
                                          });
                                        libraryRules = [];
                                        matchingBlocks.forEach((block) => {
                                          const combinedBlockConfig = [
                                            ...(block.promptsConfig?.map(
                                              (config) => ({
                                                ...config,
                                                type: "prompt",
                                              }),
                                            ) || []),
                                            ...(block.evaluableEventsConfig?.map(
                                              (config) => ({
                                                ...config,
                                                type: "evaluableEvent",
                                              }),
                                            ) || []),
                                            ...(block.librariesConfig?.map(
                                              (config) => ({
                                                ...config,
                                                type: "library",
                                              }),
                                            ) || []),
                                          ];

                                          libraryRules.push({
                                            ...block,
                                            blockConfig: combinedBlockConfig,
                                          });
                                        });
                                      } else {
                                        libraryRules = scoreRule?.filter(
                                          (rule) => {
                                            return (
                                              rule.libraryId?.id ===
                                              group.libraryId
                                            );
                                          },
                                        );
                                      }

                                      return scoreBlockRules.length > 0 ? (
                                        <ModalBlockRulesDetails
                                          scoreBlockResults={libraryRules}
                                          segmentId={segmentID}
                                          promptsData={prompts}
                                          evaluableEventsData={evaluableEvent}
                                          librariesData={libraryMatches}
                                        />
                                      ) : (
                                        <ModalPromptRulesDetails
                                          scoreRule={libraryRules}
                                        />
                                      );
                                    })()}
                                  </td>
                                </tr>
                              ),
                            );
                          })()}
                        </tbody>
                      </Table>
                    </div>
                  </Accordion>
                </div>
              </Col>
            </CardBody>
          </Card>
        </Col>
      </Container>
    </>
  );
};

export default QualityAttendance;
